import { useCallback } from 'react';

import { ResponsiveValue } from '../utils/types';

/**
 * Higher-order component that provides a `getResponsiveValue` function to the wrapped component.
 * This function returns a responsive value based on the `isMobilePreview` prop.
 *
 * @param Component - The component to wrap. It should accept a prop `getResponsiveValue` of type function.
 * @returns A new component that renders the wrapped component with the `getResponsiveValue` function.
 */
export const withResponsiveValue =
  <P extends { isMobilePreview?: boolean }>(Component: React.ComponentType<P & ResponsiveValue>) =>
  (props: P) => {
    const getResponsiveValue = useCallback(
      <T,>(value: T | { base: T; [key: string]: T }): T => {
        if (props.isMobilePreview && value && typeof value === 'object' && 'base' in value) {
          return value.base;
        }
        return value as T;
      },
      [props.isMobilePreview],
    );

    return <Component {...props} getResponsiveValue={getResponsiveValue} />;
  };
